import React from "react";
import { DndContext } from "@dnd-kit/core";
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DragOutlined } from "@ant-design/icons";
import "./DragAndDropList.scss";

const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return children({ setNodeRef, attributes, listeners, style });
};

const DragAndDropList = ({
  fields,
  move,
  form,
  fieldName,
  onReorder,
  renderItem,
  disableFirst = false,
  enabled = true,
  showHandle = true,
  onDragEndCustom,
}) => {
  const handleDragEnd = ({ active, over }) => {
    if (!over || active?.id === over?.id) return;

    if (onDragEndCustom) {
      onDragEndCustom({ active, over, fields });
      return;
    }

    const oldIndex = fields.findIndex((f) => f.key === active?.id);
    const newIndex = fields.findIndex((f) => f.key === over?.id);

    if (oldIndex < 0 || newIndex < 0) return;

    move(oldIndex, newIndex);

    const payload = {};
    form.getFieldValue(fieldName)?.forEach((e, index) => {
      if (e?.id) {
        payload[e.id] = index;
      }
    });
    onReorder?.(payload);
  };

  if (!enabled) {
    return fields.map((field, i) => (
      <div key={field.key}>{renderItem(field, i)}</div>
    ));
  }

  const sortableKeys = fields?.map((f) => f?.key);

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <SortableContext
        items={disableFirst ? sortableKeys.slice(1) : sortableKeys}
        strategy={verticalListSortingStrategy}
      >
        {fields?.map((field, i) => {
          if (disableFirst && i === 0) {
            return (
              <React.Fragment key={field.key}>
                {renderItem(field, i)}
              </React.Fragment>
            );
          }

          return (
            <SortableItem key={field.key} id={field.key}>
              {({ setNodeRef, attributes, listeners, style }) => (
                <div
                  ref={setNodeRef}
                  style={{ ...style, width: "100%" }}
                  className={showHandle ? "d-flex dnd-wrapper" : "dnd-wrapper"}
                >
                  {showHandle ? (
                    <div {...attributes} {...listeners} className="drag-handle">
                      <DragOutlined />
                    </div>
                  ) : null}
                  {renderItem(field, i, { attributes, listeners })}
                </div>
              )}
            </SortableItem>
          );
        })}
      </SortableContext>
    </DndContext>
  );
};

export default DragAndDropList;
